import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _excluded = ["headingColor", "login", "nonFormattedLogin", "onLoginBlur", "onLoginFocus", "onClose", "onBack", "showSuccessRecoverPopup", "parentSetError", "dictionary", "step", "secret", "isAllowedDataIds", "isOpen", "isDesktop", "theme", "onRecoverySuccess"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { bool, func, oneOf, shape, string } from 'prop-types';
import { Textfield } from '@s7/ui-kit';
import PasswordStrength from './PasswordStrength';
import Recovery from './Recovery';
import { Auth } from './api';
import { formatLogin, getErrorFromResponse, getLoginValidator } from './utils';
import { dictKeys, getTestIdGetter } from './utils/testIds';
import { useAnalyticsContext } from './utils/analytics';

var toMinutesAndSeconds = function toMinutesAndSeconds(seconds) {
  return [Math.floor(seconds / 60), seconds % 60].map(function (timePart) {
    return "".concat(timePart).padStart(2, '0');
  });
};

var getButtonText = function getButtonText(time, dictionary) {
  var _toMinutesAndSeconds = toMinutesAndSeconds(time),
      _toMinutesAndSeconds2 = _slicedToArray(_toMinutesAndSeconds, 2),
      minutes = _toMinutesAndSeconds2[0],
      seconds = _toMinutesAndSeconds2[1];

  return {
    buttonDisabled: true,
    buttonText: dictionary.t('com.recover.form.re_request_code').replace(/%minutes%/, minutes).replace(/%seconds%/, seconds),
    buttonTheme: 'secondary'
  };
};

var getHeadingComment = function getHeadingComment(data) {
  if (!data || !data.comment) {
    return null;
  }

  var comment = data.comment,
      notificationContact = data.notificationContact;

  if (!notificationContact) {
    return comment;
  }

  var commentParts = comment.split(notificationContact.destination);

  if (commentParts.length === 1) {
    return comment;
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, commentParts[0], /*#__PURE__*/React.createElement("b", null, notificationContact.destination), commentParts[1]);
};

export var recoverySteps = ['default', 'sended', 'confirmed'];

var RecoveryContainer = function RecoveryContainer(_ref) {
  var headingColor = _ref.headingColor,
      login = _ref.login,
      nonFormattedLogin = _ref.nonFormattedLogin,
      onLoginBlur = _ref.onLoginBlur,
      onLoginFocus = _ref.onLoginFocus,
      onClose = _ref.onClose,
      onBack = _ref.onBack,
      showSuccessRecoverPopup = _ref.showSuccessRecoverPopup,
      parentSetError = _ref.parentSetError,
      dictionary = _ref.dictionary,
      step = _ref.step,
      secret = _ref.secret,
      isAllowedDataIds = _ref.isAllowedDataIds,
      isOpen = _ref.isOpen,
      isDesktop = _ref.isDesktop,
      theme = _ref.theme,
      onRecoverySuccess = _ref.onRecoverySuccess,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState({
    secret: secret,
    password: '',
    confirmation: ''
  }),
      _useState2 = _slicedToArray(_useState, 2),
      values = _useState2[0],
      setValues = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      confirmationTooltip = _useState4[0],
      setConfirmationTooltip = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      ticketSendedToEmail = _useState6[0],
      setTicketSendedToEmail = _useState6[1];

  var _useState7 = useState(step === recoverySteps[1]),
      _useState8 = _slicedToArray(_useState7, 2),
      ticketSended = _useState8[0],
      setTicketSended = _useState8[1];

  var _useState9 = useState(step === recoverySteps[2]),
      _useState10 = _slicedToArray(_useState9, 2),
      ticketConfirmed = _useState10[0],
      setTicketConfirmed = _useState10[1];

  var _useState11 = useState(null),
      _useState12 = _slicedToArray(_useState11, 2),
      error = _useState12[0],
      setError = _useState12[1];

  var _useState13 = useState(null),
      _useState14 = _slicedToArray(_useState13, 2),
      secretError = _useState14[0],
      setSecretError = _useState14[1];

  var _useState15 = useState(),
      _useState16 = _slicedToArray(_useState15, 2),
      time = _useState16[0],
      setTime = _useState16[1];

  var _useState17 = useState(false),
      _useState18 = _slicedToArray(_useState17, 2),
      isSending = _useState18[0],
      setIsSending = _useState18[1];

  var _useState19 = useState(''),
      _useState20 = _slicedToArray(_useState19, 2),
      loginTooltip = _useState20[0],
      setLoginTooltip = _useState20[1];

  var _useState21 = useState(''),
      _useState22 = _slicedToArray(_useState21, 2),
      passwordTooltip = _useState22[0],
      setPasswordTooltip = _useState22[1];

  var _useState23 = useState(),
      _useState24 = _slicedToArray(_useState23, 2),
      headingComment = _useState24[0],
      setHeadingComment = _useState24[1];

  var intervalId = useRef(null);

  var _useAnalyticsContext = useAnalyticsContext(),
      sendEvent = _useAnalyticsContext.sendEvent;

  var getTestId = useMemo(function () {
    if (!isAllowedDataIds) {
      return function () {
        return {};
      };
    }

    return getTestIdGetter(ticketConfirmed ? dictKeys.ACCESS_RECOVERY : dictKeys.REQUEST_RECOVERY_CODE);
  }, [ticketConfirmed]);
  useEffect(function () {
    if (time) {
      intervalId.current = setInterval(function () {
        setTime(time - 1);

        if (time <= 0) {
          clearInterval(intervalId.current);
          setTime(60);
        }
      }, 1000);
    }

    return function () {
      clearInterval(intervalId.current);
    };
  }, [time]);
  useEffect(function () {
    setTicketSended(step === recoverySteps[1]);
    setTicketConfirmed(step === recoverySteps[2]);
    setHeadingComment(null);
  }, [step]);
  useEffect(function () {
    if (secret) {
      setValues(_objectSpread(_objectSpread({}, values), {}, {
        secret: secret
      }));
    }
  }, [secret]);
  useEffect(function () {
    sendEvent({
      event: 'view_screen',
      step: 'userid_input',
      flow: 'password_recovery'
    });
  }, []);
  useEffect(function () {
    if (!ticketConfirmed && ticketSended) {
      sendEvent({
        event: 'click_submit',
        step: 'confirmation_code',
        flow: 'password_recovery'
      });
    }
  }, [ticketConfirmed, ticketSended]);
  useEffect(function () {
    if (ticketConfirmed) {
      sendEvent({
        event: 'view_screen',
        step: 'confirmation_code',
        flow: 'password_recovery'
      });
    }
  }, [ticketConfirmed]);

  var validatePassword = function validatePassword() {
    var isPasswordValid = false;
    var isConfirmationValid = false;

    if (values.password.length === 0) {
      setPasswordTooltip(dictionary.t('com.validation.required'));
    } else if (values.password.length < 6) {
      setPasswordTooltip(dictionary.t('com.validation.password.weak'));
    } else if ( // eslint-disable-next-line no-useless-escape
    !/(?=.*[!№"#$%&'()*+,\-.\/:;<>=?@\]\[{}\\^_`~|])/.test(values.password)) {
      setPasswordTooltip(dictionary.t('com.validation.password.weak'));
    } else if (!/(?=.*\d)/.test(values.password)) {
      setPasswordTooltip(dictionary.t('com.validation.password.weak'));
    } else if (!/(?=.*[a-zа-яё])/.test(values.password)) {
      setPasswordTooltip(dictionary.t('com.validation.password.weak'));
    } else if (!/(?=.*[A-ZА-ЯЁ])/.test(values.password)) {
      setPasswordTooltip(dictionary.t('com.validation.password.weak'));
    } else {
      setPasswordTooltip('');
      isPasswordValid = true;
    }

    if (values.password !== values.confirmation) {
      setConfirmationTooltip(dictionary.t('com.validation.password_mismatch'));
    } else {
      setConfirmationTooltip('');
      isConfirmationValid = true;
    }

    return isPasswordValid && isConfirmationValid;
  };

  var handleChange = useCallback(function (event) {
    setValues(_objectSpread(_objectSpread({}, values), {}, _defineProperty({}, event.target.name || 'password', event.target.value)));
  }, [values]);
  var handleSecretChange = useCallback(function (event) {
    handleChange(_objectSpread(_objectSpread({}, event), {}, {
      target: _objectSpread(_objectSpread({}, event.target), {}, {
        name: 'secret'
      })
    }));

    if (secretError) {
      setSecretError(null);
    }
  }, [handleChange, secretError]);

  var handleLoginChange = function handleLoginChange(event) {
    props.handleLoginChange(event);
    setLoginTooltip('');
  };

  var validateLogin = useCallback(getLoginValidator({
    dictionary: dictionary,
    tooltipSetter: setLoginTooltip
  }), []);
  useEffect(function () {
    validatePassword();
  }, [values.password, values.confirmation]);

  var handlePasswordBlur = function handlePasswordBlur() {
    validatePassword();
  };

  var handleConfirmationChange = function handleConfirmationChange(e) {
    handleChange(_objectSpread(_objectSpread({}, e), {}, {
      target: _objectSpread(_objectSpread({}, e.target), {}, {
        name: 'confirmation'
      })
    }));

    if (values.password === e.currentTarget.value) {
      setConfirmationTooltip('');
    }
  };

  var handleError = function handleError(err) {
    setError(getErrorFromResponse(err));
    setTicketSendedToEmail(false);
    setHeadingComment(null);
  };

  var handleSecretError = function handleSecretError(err) {
    setValues(_objectSpread(_objectSpread({}, values), {}, {
      secret: ''
    }));
    setSecretError(getErrorFromResponse(err));
  };

  var sendRecoveryTicket = function sendRecoveryTicket() {
    if (!validateLogin(login)) {
      return;
    }

    var _formatLogin = formatLogin(login),
        type = _formatLogin.type;

    setIsSending(true);

    if (secretError) {
      setSecretError(null);
    }

    Auth.sendRecoveryTicket({
      id: nonFormattedLogin
    }).then(function (response) {
      setError(null);
      setTicketSended(true);
      setTime(60);
      setTicketSendedToEmail(type === 'email');
      setHeadingComment(getHeadingComment(response.data));
    }).catch(handleError).finally(function () {
      setIsSending(false);
    });
  };

  var checkRecoveryTicket = function checkRecoveryTicket() {
    setIsSending(true);
    Auth.checkRecoveryTicket({
      id: nonFormattedLogin,
      secret: values.secret,
      type: 'RECOVERY'
    }).then(function () {
      setSecretError(null);
      setTicketConfirmed(true);
    }).catch(handleSecretError).finally(function () {
      return setIsSending(false);
    });
  };

  var updateCredentialsByRecoveryTicket = function updateCredentialsByRecoveryTicket() {
    if (!validatePassword()) {
      return;
    }

    setIsSending(true);
    Auth.updateCredentialsByRecoveryTicket({
      id: nonFormattedLogin,
      secret: values.secret,
      password: values.password
    }).then(function () {
      parentSetError(null);

      if (onRecoverySuccess) {
        onRecoverySuccess();
      } else {
        onBack();
        showSuccessRecoverPopup();
      }

      sendEvent({
        event: 'submit_success',
        flow: 'password_recovery',
        step: 'confirmation_code'
      });
    }).catch(handleError).finally(function () {
      return setIsSending(false);
    });
  };

  var onButtonClick = sendRecoveryTicket;
  var buttonText = dictionary.t('com.recover.form.request_code');
  var buttonProps = {
    buttonTheme: theme
  };

  if (ticketConfirmed) {
    onButtonClick = updateCredentialsByRecoveryTicket;
    buttonText = dictionary.t('com.global.save');
  } else if (ticketSended) {
    if (!values.secret) {
      if (time > 0) {
        buttonProps = getButtonText(time, dictionary);
      } else {
        onButtonClick = sendRecoveryTicket;
        buttonText = dictionary.t('com.recover.form.resend_code');
        buttonProps = {
          buttonTheme: theme
        };
      }
    } else {
      onButtonClick = checkRecoveryTicket;
      buttonText = dictionary.t('com.global.continue');
      buttonProps = {
        buttonTheme: theme
      };
    }
  }

  var getHeadingText = useCallback(function () {
    if (ticketConfirmed) {
      return dictionary.t('com.recover.info.password_recommendation', {
        html: true,
        tag: 'div'
      });
    }

    if (headingComment) {
      return headingComment;
    }

    return dictionary.t(ticketSendedToEmail ? 'com.recover.info.email' : 'com.recover.info');
  }, [dictionary, ticketConfirmed, ticketSendedToEmail, headingComment]);
  return /*#__PURE__*/React.createElement(Recovery, _extends({
    backTitle: dictionary.t('com.recover.login'),
    buttonDisabled: isSending,
    buttonText: buttonText,
    dictionary: dictionary,
    heading: getHeadingText(),
    headingColor: headingColor,
    isDesktop: isDesktop,
    isOpen: isOpen,
    notification: error && error.message || secretError && secretError.message,
    onBack: onBack,
    onButtonClick: onButtonClick,
    onClose: onClose,
    title: dictionary.t('com.recover.title'),
    wrapHeading: !ticketConfirmed
  }, buttonProps, {
    fields: /*#__PURE__*/React.createElement(React.Fragment, null, !ticketConfirmed && !ticketSended && /*#__PURE__*/React.createElement(Textfield, {
      disabled: Boolean(values.secret),
      inputProps: getTestId('login'),
      invalid: Boolean(loginTooltip) || Boolean(error),
      label: dictionary.t('com.login.form.login'),
      name: "login",
      onBlur: onLoginBlur,
      onChange: handleLoginChange,
      onFocus: onLoginFocus,
      theme: theme,
      tooltip: loginTooltip,
      value: login
    }), !ticketConfirmed && ticketSended && /*#__PURE__*/React.createElement(Textfield, {
      inputProps: getTestId('code'),
      invalid: Boolean(secretError),
      label: dictionary.t("com.recover.form.".concat(ticketSendedToEmail ? 'email' : 'sms', "_code")),
      name: "secret",
      onChange: handleSecretChange,
      theme: theme,
      value: values.secret
    }), ticketConfirmed && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PasswordStrength, {
      dictionary: dictionary,
      inputProps: getTestId('password'),
      invalid: Boolean(passwordTooltip),
      isDesktop: isDesktop,
      label: dictionary.t('com.recover.new_password'),
      name: "password",
      onBlur: handlePasswordBlur,
      onChange: handleChange,
      theme: theme,
      tooltip: passwordTooltip,
      type: "password",
      value: values.password
    }), /*#__PURE__*/React.createElement(Textfield, {
      inputProps: getTestId('passwordConfirmation'),
      invalid: Boolean(confirmationTooltip),
      label: dictionary.t('com.recover.form.password_confirmation'),
      name: "confirmation",
      onChange: handleConfirmationChange,
      onInput: handleConfirmationChange,
      theme: theme,
      tooltip: confirmationTooltip,
      type: "password",
      value: values.confirmation,
      withoutEye: true
    }))),
    getTestId: getTestId,
    ticketConfirmed: ticketConfirmed,
    isSecretEntered: Boolean(values.secret)
  }, props));
};

RecoveryContainer.propTypes = {
  isDesktop: bool,
  codeTooltip: string,
  dictionary: shape({
    t: func
  }),
  getTestId: func,
  handleLoginChange: func,
  headingColor: string,
  isAllowedDataIds: bool,
  isOpen: bool,
  login: string,
  nonFormattedLogin: string,
  onBack: func,
  onClose: func,
  onLoginBlur: func,
  onLoginFocus: func,
  onRecoverySuccess: func,
  parentSetError: func,
  secret: string,
  showSuccessRecoverPopup: func,
  step: oneOf(recoverySteps),
  theme: oneOf(['b2c', 'b2b'])
};
RecoveryContainer.defaultProps = {
  step: recoverySteps[0],
  secret: ''
};
export default RecoveryContainer;